import React from "react";
import { useCallback, useEffect, useRef } from "react";
import { Coordinate } from "ol/coordinate";
import Feature from "ol/Feature";
import { Polygon } from "ol/geom";
import { fromLonLat } from "ol/proj";
import Style from "ol/style/Style";
import Stroke from "ol/style/Stroke";
import Fill from "ol/style/Fill";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { Text } from "ol/style";
import { Annotation } from ".";
import { makeText } from "../../utils/object";
import { useMap } from "../../hooks/incontext/useMap";
import { ANNOTATION_COLOR } from "../../constants/color";
import { useInteractionEvent } from "../../hooks/incontext/useInteractionEvent";
import { Geometry } from "ol/geom";

/** `ANNOTATION_COLOR` 팔레트 밖의 색이 필요한 호출자를 위한 선·채움 지정 — CSS 색 문자열(rgba 허용) */
export interface CustomPolygonStyleOverride {
  strokeColor: string;
  /** 선 폭(px). 기본 2 — 팔레트 스타일과 같은 값 */
  strokeWidth?: number;
  fillColor: string;
}

export interface CustomPolygonProps extends Annotation {
  positions: Coordinate[][];
  /**
   * 있으면 `color`·`opacity` 대신 이 선·채움으로 그린다. 팔레트 밖의 색이 필요한 호출자(지오로그 조회 영역)가
   * 쓴다. 라벨(`children`)·선택·팝업 동작은 그대로다.
   */
  styleOverride?: CustomPolygonStyleOverride;
}

const DEFAULT_STROKE_WIDTH = 2;

function resolveStroke(
  color: keyof typeof ANNOTATION_COLOR,
  opacity: number,
  override?: CustomPolygonStyleOverride
): Stroke {
  return override
    ? new Stroke({
        color: override.strokeColor,
        width: override.strokeWidth ?? DEFAULT_STROKE_WIDTH,
      })
    : new Stroke({
        color: ANNOTATION_COLOR[color].stroke(opacity),
        width: DEFAULT_STROKE_WIDTH,
      });
}

function resolveFill(
  color: keyof typeof ANNOTATION_COLOR,
  opacity: number,
  override?: CustomPolygonStyleOverride
): Fill {
  return new Fill({
    color: override ? override.fillColor : ANNOTATION_COLOR[color].fill(opacity),
  });
}

export const CustomPolygon = ({
  positions,
  color = "BLUE",
  properties = {},
  onClick,
  onHover,
  onLeave,
  zIndex = 0,
  children,
  opacity = 1,
  isDisabledSelection = false,
  styleOverride,
}: CustomPolygonProps) => {
  const map = useMap();
  const annotationRef = useRef<Feature<Polygon>>(
    new Feature(
      new Polygon(
        positions.map((pos) => pos.map((position) => fromLonLat(position)))
      )
    )
  );

  const annotationLayerRef = useRef<
    VectorLayer<VectorSource<Feature<Geometry>>>
  >(
    new VectorLayer({
      source: new VectorSource({
        wrapX: false,
        features: [annotationRef.current],
      }),
    })
  );

  const annotationStyleRef = useRef(
    new Style({
      stroke: resolveStroke(color, opacity, styleOverride),
      fill: resolveFill(color, opacity, styleOverride),
      text:
        children && !children.props.isPopup
          ? makeText({
              text: children.props.children || "",
              size: children.props.size || 15,
              color: children.props.color ? children.props.color : "black",
              outline: children.props.outline,
              isMarker: false,
            })
          : undefined,
    })
  );

  const onClickHandler = useCallback(() => {
    if (onClick) {
      onClick({
        annotation: annotationRef.current,
        properties,
      });
    }
    // 선택된 Feature에 대한 작업 수행
    // 예: 스타일 변경, 정보 표시 등
  }, [onClick, properties]);

  const onHoverHandler = useCallback(
    (feature: Feature) => {
      if (feature) {
        if (onHover) {
          onHover({ annotation: annotationRef.current, properties });
        }
      }

      // 수정중일땐 팝업 관여하지 않음
      if (map.getProperties().isModifying) return;

      // Pop up text
      if (feature && children?.props.isPopup) {
        const hoveredFeature = feature;

        const hoveredFeatureStyle = hoveredFeature.getStyle() as Style;
        hoveredFeatureStyle.setText(
          makeText({
            text: children.props.children || "",
            size: children.props.size || 15,
            color: children.props.color ? children.props.color : "black",
            outline: children.props.outline,
            isMarker: false,
          })
        );

        annotationRef.current.setStyle(hoveredFeatureStyle);
      } else if (feature && children?.props.isPopup) {
        const hoveredFeatureStyle = annotationRef.current.getStyle() as Style;

        hoveredFeatureStyle.setText(new Text());
        annotationRef.current.setStyle(hoveredFeatureStyle);
      }
    },
    [children, map, onHover, properties]
  );

  const overrideStroke = styleOverride?.strokeColor;
  const overrideWidth = styleOverride?.strokeWidth;
  const overrideFill = styleOverride?.fillColor;
  useEffect(() => {
    const override =
      overrideStroke !== undefined && overrideFill !== undefined
        ? {
            strokeColor: overrideStroke,
            strokeWidth: overrideWidth,
            fillColor: overrideFill,
          }
        : undefined;
    annotationStyleRef.current.setFill(resolveFill(color, opacity, override));
    annotationStyleRef.current.setStroke(
      resolveStroke(color, opacity, override)
    );
    // 스타일 객체를 제자리에서 바꾸므로 피처를 다시 그리게 한다
    annotationRef.current.changed();
  }, [opacity, color, overrideStroke, overrideWidth, overrideFill]);

  useInteractionEvent({
    annotation: annotationLayerRef.current,
    onClick: onClickHandler,
    onHover: onHoverHandler,
    isDisabledSelection,
  });

  useEffect(() => {
    if (annotationRef.current) {
      const geometry = annotationRef.current.getGeometry() as Polygon;
      geometry.setCoordinates([
        positions[0].map((position) => fromLonLat(position)),
      ]);
    }
  }, [positions]);

  useEffect(() => {
    if (annotationLayerRef.current) {
      annotationLayerRef.current.setZIndex(zIndex);
    }
  }, [zIndex]);

  useEffect(() => {
    if (annotationLayerRef.current && children) {
      annotationStyleRef.current.setText(
        makeText({
          text: children.props.children || "",
          size: children.props.size || 15,
          color: children.props.color ? children.props.color : "black",
          outline: children.props.outline,
          isMarker: false,
        })
      );
    }
  }, [children]);
  useEffect(() => {
    if (!children) {
      annotationStyleRef.current.setText(new Text());
    }
  }, [children]);

  // 레이어 생명주기 — map 변경 시에만 마운트/언마운트
  useEffect(() => {
    const annotationLayerCurrent = annotationLayerRef.current;
    annotationRef.current.setStyle(annotationStyleRef.current);
    map.addLayer(annotationLayerCurrent);

    return () => {
      annotationLayerCurrent.getSource()?.clear();
      map.removeLayer(annotationLayerCurrent);
    };
  }, [map]);

  // 프로퍼티 업데이트
  useEffect(() => {
    annotationRef.current.setProperties({
      ...properties,
      shape: "Polygon",
      isModifying: false,
      source: annotationLayerRef.current.getSource(),
      layer: annotationLayerRef.current,
      hasPopup: children ? children?.props.isPopup : false,
    });
  }, [properties, children]);

  return null;
};
